import React, { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import lottoApi from "../api";
import { useAuth } from "../../contexts/JWTContext";
import { money, useAsync, pushToast } from "../lib";
import { Empty, Skeleton } from "../ui";

// inviteSetting.inviteStatus: OFF | FIRSTTIME (เฉพาะบิลแรกของเพื่อน) | ALLTIME (ทุกบิล)
const MODE_TEXT = { FIRSTTIME: "บิลแรก", ALLTIME: "ทุกบิล" };

function fmtDate(v, withTime = false) {
  if (!v) return "-";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("th-TH", {
    day: "numeric",
    month: "short",
    year: "2-digit",
    ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  });
}

const maskUsername = (username) => {
  const raw = String(username || "");
  return raw.length <= 4 ? "xxxx" : `${raw.slice(0, -4)}xxxx`;
};

async function copyText(text, inputEl) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // http / webview เก่าไม่มี clipboard API — เลือกข้อความใน input แล้วใช้ execCommand แทน
    try {
      inputEl?.select();
      return document.execCommand("copy");
    } catch {
      return false;
    }
  }
}

function Avatar({ name, tone = "a" }) {
  return <div className={`lx-ref-avatar tone-${tone}`}>{String(name || "?").charAt(0).toUpperCase()}</div>;
}

function HowItWorks({ setting }) {
  const pct = Number(setting?.ndpercent) || 0;
  const minDep = Number(setting?.minimumdeposit) || 0;
  const mode = MODE_TEXT[setting?.inviteStatus] || "บิลแรก";
  const exampleDep = Math.max(1000, minDep);
  const steps = [
    {
      icon: "fluent-emoji-flat:megaphone",
      title: "แชร์ลิงก์",
      desc: "ส่งลิงก์ของคุณให้เพื่อนทาง LINE หรือ Facebook",
    },
    {
      icon: "fluent-emoji-flat:handshake",
      title: "เพื่อนสมัครและฝาก",
      desc: minDep ? `ฝากขั้นต่ำ ${money(minDep)} บาท (นับ${mode})` : `นับยอดฝาก${mode}ของเพื่อน`,
    },
    {
      icon: "fluent-emoji-flat:money-bag",
      title: `รับ ${pct}% เป็นเครดิต`,
      desc: "กดรับได้ที่แท็บ \"โบนัสรอรับ\" ด้านล่าง",
    },
  ];
  return (
    <section className="vx-block vx-how">
      <div className="vx-block-head">
        <div className="vx-block-title">
          <span className="vx-block-ic cat-b">
            <Icon icon="solar:lightbulb-bolt-bold-duotone" width={18} height={18} />
          </span>
          <h2 className="lx-h2">ได้โบนัสยังไง</h2>
        </div>
      </div>
      <ol className="vx-steps">
        {steps.map((s, i) => (
          <li className="vx-step" key={s.title}>
            <span className="vx-step-no">{i + 1}</span>
            <span className="vx-step-ic">
              <Icon icon={s.icon} width={38} height={38} />
            </span>
            <div className="vx-step-tx">
              <b>{s.title}</b>
              <p>{s.desc}</p>
            </div>
          </li>
        ))}
      </ol>
      {pct > 0 && (
        <div className="vx-example">
          <Icon icon="solar:calculator-minimalistic-bold-duotone" width={18} />
          <span>
            ตัวอย่าง: เพื่อนฝาก <b>{money(exampleDep)}</b> บาท → คุณได้{" "}
            <b className="vx-example-plus">+{money(Math.floor((exampleDep * pct) / 100))}</b> บาท
          </span>
        </div>
      )}
    </section>
  );
}

function Conditions({ setting }) {
  if (!setting?.inviteDetail && !setting?.withdrawConditionDetail) return null;
  return (
    <details className="lx-ref-terms">
      <summary>
        <Icon icon="solar:document-text-bold-duotone" width={18} />
        เงื่อนไขและรายละเอียด
        <Icon icon="solar:alt-arrow-down-linear" width={16} className="lx-ref-terms-caret" />
      </summary>
      {setting.inviteDetail && (
        <p>
          <Icon icon="solar:lightbulb-bold" width={14} />
          {setting.inviteDetail}
        </p>
      )}
      {setting.withdrawConditionDetail && (
        <p>
          <Icon icon="solar:pin-bold" width={14} />
          {setting.withdrawConditionDetail}
        </p>
      )}
    </details>
  );
}

export default function ReferFriendPage({ onDone }) {
  const { user, isAuthenticated } = useAuth();
  const nav = useNavigate();
  const uid = user?.id;
  const linkRef = useRef(null);

  const { data: setting, loading: settingLoading } = useAsync(() => lottoApi.inviteSetting().catch(() => null), []);
  const {
    data: invites,
    loading: invitesLoading,
    reload: reloadInvites,
  } = useAsync(() => (uid ? lottoApi.inviteSummary(uid) : Promise.resolve([])), [uid]);
  const { data: invitedUsers, loading: usersLoading } = useAsync(
    () => (uid ? lottoApi.invitedUsers(uid) : Promise.resolve([])),
    [uid],
  );

  const [tab, setTab] = useState("pending");
  const [claimingId, setClaimingId] = useState(null);
  const [copied, setCopied] = useState(false);

  const pending = useMemo(() => (invites || []).filter((r) => r.inviteStatus === "UNRECEIVE"), [invites]);
  const claimed = useMemo(() => (invites || []).filter((r) => r.inviteStatus !== "UNRECEIVE"), [invites]);
  const totalEarned = claimed.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
  const pendingTotal = pending.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);

  // เพื่อนที่ฝากแล้ว = มีแถวใน inviteHistory (ระบบสร้างโบนัสตอนเพื่อนฝากถึงขั้นต่ำ)
  const depositedNames = useMemo(() => new Set((invites || []).map((r) => r.username)), [invites]);
  const friends = invitedUsers || [];
  const depositedCount = friends.filter((f) => depositedNames.has(f.username)).length;

  const isOff = !settingLoading && (!setting || setting.inviteStatus === "OFF");
  const pct = Number(setting?.ndpercent) || 0;
  // รับหลายรายการ = ส่ง ALL ครั้งเดียว (เซิร์ฟเวอร์ตรวจเครดิต 0 ครั้งเดียวแล้วจ่ายทุกรายการ)
  const canClaimAll = pending.length > 1;

  const referralLink = `${typeof window !== "undefined" ? window.location.origin : ""}/auth?friend=${uid || ""}`;
  const shareText = `สมัครผ่านลิงก์ของฉัน แล้วมาเล่นหวยด้วยกัน ${referralLink}`;

  const onCopy = async () => {
    if (await copyText(referralLink, linkRef.current)) {
      setCopied(true);
      pushToast("คัดลอกลิงก์แล้ว", "ok", 1500);
      setTimeout(() => setCopied(false), 2000);
    } else {
      pushToast("คัดลอกไม่สำเร็จ — กดค้างที่ลิงก์เพื่อคัดลอกเอง", "err");
    }
  };

  const onNativeShare = () => {
    navigator.share({ title: "แนะนำเพื่อน", text: "สมัครผ่านลิงก์ของฉัน แล้วมาเล่นหวยด้วยกัน", url: referralLink }).catch(() => {});
  };

  // รับโบนัส — กฎกลาง: เครดิตในกระเป๋าต้องเหลือ 0 ก่อนรับ (เซิร์ฟเวอร์ตอบเหตุผลมาใน message) จึงรับต่อเนื่องทีละใบไม่ได้ → "รับทั้งหมด" ส่ง ALL ครั้งเดียว
  const claimRaw = async (what) => {
    try {
      return await lottoApi.claimInvite(uid, what);
    } catch (e) {
      return { success: false, message: e?.response?.data?.message };
    }
  };

  const claim = async (inviteId) => {
    setClaimingId(inviteId);
    try {
      const res = await claimRaw(inviteId);
      if (res?.success === true) {
        pushToast(res.turnover_required > 0 ? "รับโบนัสเรียบร้อย — โบนัสนี้ติดเทิร์น แทงให้ครบก่อนถอน" : "รับโบนัสเรียบร้อย เครดิตเข้ากระเป๋าแล้ว", "ok");
        onDone?.();
      } else {
        pushToast(res?.message || "รับโบนัสไม่สำเร็จ กรุณาติดต่อแอดมิน", "err", 6000);
      }
    } finally {
      setClaimingId(null);
      reloadInvites();
    }
  };

  const claimAll = async () => {
    setClaimingId("all");
    try {
      const res = await claimRaw("ALL");
      if (res?.success === true) {
        pushToast(`รับโบนัสครบ ${res.count || pending.length} รายการแล้ว`, "ok");
        onDone?.();
      } else {
        pushToast(res?.message || "รับโบนัสไม่สำเร็จ กรุณาติดต่อแอดมิน", "err", 6000);
      }
    } finally {
      setClaimingId(null);
      reloadInvites();
    }
  };

  const hero = (
    <div className="vx-poster">
      <div className="vx-poster-rays" aria-hidden="true" />
      <div className="vx-poster-art" aria-hidden="true">
        <Icon icon="fluent-emoji-flat:wrapped-gift" className="a1" />
        <Icon icon="fluent-emoji-flat:money-bag" className="a2" />
        <Icon icon="fluent-emoji-flat:sparkles" className="a3" />
      </div>
      <div className="vx-poster-kicker">
        <Icon icon="fluent-emoji-flat:busts-in-silhouette" width={18} height={18} />
        แนะนำเพื่อน
      </div>
      <h1 className="vx-poster-title">
        {isOff ? (
          "ปิดรับชั่วคราว"
        ) : (
          <>
            รับ <span className="vx-poster-pct">{settingLoading ? "…" : `${pct}%`}</span>
            <br />
            จากยอดฝาก{MODE_TEXT[setting?.inviteStatus] || ""}ของเพื่อน
          </>
        )}
      </h1>
      <p className="vx-poster-sub">
        {isOff
          ? "ระบบแนะนำเพื่อนปิดอยู่ตอนนี้ โบนัสที่รอรับยังกดรับได้ตามปกติ"
          : "ชวนได้ไม่จำกัด ยิ่งชวนเยอะ ยิ่งได้เยอะ"}
      </p>

      {isAuthenticated && !isOff && (
        <div className="vx-coupon">
          <div className="vx-coupon-label">
            <Icon icon="fluent-emoji-flat:link" width={16} height={16} />
            ลิงก์แนะนำของคุณ
          </div>
          <div className="vx-coupon-row">
            <input
              ref={linkRef}
              readOnly
              value={referralLink}
              className="vx-coupon-link lx-mono"
              onFocus={(e) => e.target.select()}
              aria-label="ลิงก์แนะนำของคุณ"
            />
            <button className={`vx-coupon-copy ${copied ? "done" : ""}`} onClick={onCopy}>
              <Icon icon={copied ? "solar:check-circle-bold" : "solar:copy-bold-duotone"} width={18} />
              {copied ? "คัดลอกแล้ว" : "คัดลอก"}
            </button>
          </div>
          <div className="vx-share">
            <a
              className="vx-share-btn line"
              href={`https://line.me/R/share?text=${encodeURIComponent(shareText)}`}
              target="_blank"
              rel="noreferrer"
            >
              <Icon icon="simple-icons:line" width={18} /> LINE
            </a>
            <a
              className="vx-share-btn fb"
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}`}
              target="_blank"
              rel="noreferrer"
            >
              <Icon icon="simple-icons:facebook" width={17} /> Facebook
            </a>
            {typeof navigator !== "undefined" && navigator.share && (
              <button className="vx-share-btn" onClick={onNativeShare}>
                <Icon icon="solar:share-bold" width={17} /> แชร์
              </button>
            )}
          </div>
        </div>
      )}

      {!isAuthenticated && (
        <div className="vx-poster-cta">
          <button className="lx-btn mint" onClick={() => nav("/auth")}>
            <Icon icon="solar:login-3-bold" width={18} />
            เข้าสู่ระบบเพื่อรับลิงก์ของคุณ
          </button>
        </div>
      )}
    </div>
  );

  if (!isAuthenticated) {
    return (
      <div className="vx-ref" style={{ marginTop: 18 }}>
        <div className="vx-ref-side">{hero}</div>
        <div className="vx-ref-main">
          {!settingLoading && !isOff && <HowItWorks setting={setting} />}
          <Conditions setting={setting} />
        </div>
      </div>
    );
  }

  const stats = [
    { tone: "a", icon: "fluent-emoji-flat:busts-in-silhouette", label: "เพื่อนทั้งหมด", value: friends.length, unit: "คน" },
    { tone: "b", icon: "fluent-emoji-flat:money-with-wings", label: "เพื่อนที่ฝากแล้ว", value: depositedCount, unit: "คน" },
    { tone: "c", icon: "fluent-emoji-flat:hourglass-not-done", label: "โบนัสรอรับ", value: money(pendingTotal), unit: "บาท" },
    { tone: "d", icon: "fluent-emoji-flat:check-mark-button", label: "รับไปแล้ว", value: money(totalEarned), unit: "บาท" },
  ];

  const tabs = [
    { id: "pending", label: "โบนัสรอรับ", count: pending.length },
    { id: "friends", label: "เพื่อนของฉัน", count: friends.length },
    { id: "history", label: "ประวัติรับโบนัส" },
  ];

  return (
    <div className="vx-ref" style={{ marginTop: 18 }}>
      <div className="vx-ref-side">{hero}</div>

      <div className="vx-ref-main">
        <div className="vx-stats">
          {stats.map((s) => (
            <div className={`vx-stat tone-${s.tone}`} key={s.label}>
              <span className="vx-stat-ic">
                <Icon icon={s.icon} width={30} height={30} />
              </span>
              <div>
                <div className="vx-stat-val">
                  {invitesLoading || usersLoading ? "–" : s.value}
                  <small>{s.unit}</small>
                </div>
                <div className="vx-stat-label">{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {!settingLoading && !isOff && <HowItWorks setting={setting} />}

        <section className="vx-block">
          <div className="lx-seg vx-ref-tabs">
            {tabs.map((t) => (
              <button key={t.id} className={tab === t.id ? "on" : ""} onClick={() => setTab(t.id)}>
                {t.label}
                {!!t.count && <span className="lx-ref-tab-count">{t.count}</span>}
              </button>
            ))}
          </div>

        <div style={{ marginTop: 12 }}>
          {tab === "pending" &&
            (invitesLoading ? (
              <Skeleton h={140} />
            ) : !pending.length ? (
              <div className="lx-card">
                <Empty icon="solar:gift-bold-duotone">
                  ยังไม่มีโบนัสรอรับ
                  <div className="lx-muted" style={{ fontSize: 12, marginTop: 4 }}>
                    เมื่อเพื่อนฝากเงินครบเงื่อนไข โบนัสจะขึ้นที่นี่ให้กดรับ
                  </div>
                </Empty>
              </div>
            ) : (
              <>
                <div className="lx-ref-claimbar">
                  <div>
                    <div className="lx-muted" style={{ fontSize: 12 }}>
                      รอรับทั้งหมด {pending.length} รายการ
                    </div>
                    <div className="lx-ref-claimbar-total">+{money(pendingTotal)} บาท</div>
                  </div>
                  {canClaimAll && (
                    <button className="lx-btn mint" disabled={!!claimingId} onClick={claimAll}>
                      {claimingId === "all" ? "กำลังรับ..." : "รับทั้งหมด"}
                    </button>
                  )}
                </div>
                <div className="lx-ref-note">
                  <Icon icon="solar:info-circle-bold-duotone" width={16} />
                  {setting?.withdrawConditionType && setting.withdrawConditionType !== "NONE" && Number(setting.withdrawConditionAmount) > 0
                    ? `โบนัสนี้ติดเทิร์น ${Number(setting.withdrawConditionAmount)} เท่า — แทงหวยให้ครบก่อนถอน · `
                    : "โบนัสนี้ไม่ติดเทิร์น ถอนได้ทันที · "}
                  กดรับได้เมื่อเครดิตในกระเป๋าเหลือ 0
                </div>
                <div className="lx-list">
                  {pending.map((row) => (
                    <div className="lx-ref-row" key={row.id}>
                      <Avatar name={row.username} tone="b" />
                      <div className="lx-ref-row-main">
                        <div className="lx-ref-row-name">{maskUsername(row.username)}</div>
                        <div className="lx-ref-row-sub">
                          ฝาก {money(row.topupAmount)} · {row.percent}% · {fmtDate(row.created_at)}
                        </div>
                      </div>
                      <div className="lx-ref-row-amt">+{money(row.amount)}</div>
                      <button
                        className="lx-btn mint sm"
                        disabled={!!claimingId}
                        onClick={() => claim(row.id)}
                      >
                        {claimingId === row.id ? "..." : "รับ"}
                      </button>
                    </div>
                  ))}
                </div>
              </>
            ))}

          {tab === "friends" &&
            (usersLoading ? (
              <Skeleton h={140} />
            ) : !friends.length ? (
              <div className="lx-card">
                <Empty icon="solar:users-group-rounded-bold-duotone">
                  ยังไม่มีเพื่อนสมัครผ่านลิงก์ของคุณ
                  {!isOff && (
                    <div style={{ marginTop: 12 }}>
                      <button className="lx-btn primary sm" onClick={onCopy}>
                        คัดลอกลิงก์ไปชวนเพื่อน
                      </button>
                    </div>
                  )}
                </Empty>
              </div>
            ) : (
              <div className="lx-list">
                {friends.map((row, i) => {
                  const deposited = depositedNames.has(row.username);
                  return (
                    <div className="lx-ref-row" key={`${row.username}-${i}`}>
                      <Avatar name={row.username} tone="a" />
                      <div className="lx-ref-row-main">
                        <div className="lx-ref-row-name">{maskUsername(row.username)}</div>
                        <div className="lx-ref-row-sub">สมัคร {fmtDate(row.created_at)}</div>
                      </div>
                      <span className={`lx-ref-status ${deposited ? "ok" : ""}`}>
                        {deposited ? "ฝากแล้ว" : "ยังไม่ฝาก"}
                      </span>
                    </div>
                  );
                })}
              </div>
            ))}

          {tab === "history" &&
            (invitesLoading ? (
              <Skeleton h={140} />
            ) : !claimed.length ? (
              <div className="lx-card">
                <Empty icon="solar:history-bold-duotone">ยังไม่มีประวัติการรับโบนัส</Empty>
              </div>
            ) : (
              <div className="lx-list">
                {[...claimed]
                  .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                  .map((row) => (
                    <div className="lx-ref-row" key={row.id}>
                      <Avatar name={row.username} tone="d" />
                      <div className="lx-ref-row-main">
                        <div className="lx-ref-row-name">{maskUsername(row.username)}</div>
                        <div className="lx-ref-row-sub">
                          ฝาก {money(row.topupAmount)} · {fmtDate(row.created_at, true)}
                        </div>
                      </div>
                      <div className="lx-ref-row-amt">+{money(row.amount)}</div>
                      <span className="lx-ref-status ok">รับแล้ว</span>
                    </div>
                  ))}
              </div>
            ))}
        </div>
        </section>

        <Conditions setting={setting} />
      </div>
    </div>
  );
}
